<template>
  <el-popover
    v-if="interactionConfig && interactionConfig.responseType && isPopoverMode"
    :placement="popoverPlacement"
    :trigger="popoverTrigger"
    :width="interactionConfig.popoverWidth || 400"
    :popper-class="interactionConfig.popupClass || ''"
  >
    <template #reference>
      <span class="click-config-overlay click-config-overlay--interactive"></span>
    </template>
    <PopoverPageRender
      :area-data="interactionConfig.popoverPageData"
      :width="interactionConfig.popoverWidth || 400"
    />
  </el-popover>

  <span
    v-else-if="interactionConfig && interactionConfig.responseType"
    class="click-config-overlay"
    :class="{ 'click-config-overlay--interactive': isInteractive }"
    @click="handleClick"
  ></span>

  <el-dialog
    v-if="interactionConfig && interactionConfig.responseType === 'popup'"
    :class="interactionConfig.popupClass || ''"
    :title="interactionConfig.popupTitle || ''"
    append-to-body
    v-model="dialogTableVisible"
  >
    <template #default>
      <div
        v-if="dialogTableVisible && articleContent && articleContent.content"
        class="new-detail-item-cont"
        v-html="articleContent.content"
      ></div>
    </template>
    <template #footer>
      <el-button type="primary" @click="dialogTableVisible = false">
        {{ getLocaleText('关闭') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { getArticleDetails } from "@/api";
import { getSafeLink, isTarget_Blank } from "@/utils/globalData.js";
import PopoverPageRender from "@/components/render/PopoverPageRender.vue";
import useLocate from "../uses/useLocate.js";

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { getLocaleText } = useLocate();
const dialogTableVisible = ref(false);
const articleContent = ref(null);

const interactionConfig = computed(() => props.config || {});

const isInteractive = computed(() => {
  return !!interactionConfig.value?.responseType;
});

const isPopoverMode = computed(() => {
  return (
    interactionConfig.value?.responseType === "popoverPage" &&
    !!interactionConfig.value?.popoverPageData
  );
});

const popoverPlacement = computed(() => {
  return interactionConfig.value?.popoverPlacement || "right";
});

const popoverTrigger = computed(() => {
  return interactionConfig.value?.popoverTrigger || "hover";
});

const safeLink = computed(() => {
  if (
    !["link", "jumpLink"].includes(
      interactionConfig.value?.responseType || ""
    )
  ) {
    return "";
  }
  return getSafeLink(interactionConfig.value?.link);
});

const getArticleContent = async () => {
  if (
    interactionConfig.value?.responseType !== "popup" ||
    !interactionConfig.value?.article?.id
  ) {
    articleContent.value = null;
    return;
  }
  const res = await getArticleDetails({
    pageNo: 0,
    pageSize: 9999,
    id: interactionConfig.value.article.id,
    status: "1",
  });
  if (res.data.dataList) {
    articleContent.value = res.data.dataList[0];
    if (articleContent.value.type === 1 && articleContent.value.content) {
      articleContent.value.content = articleContent.value.content.replace(
        "video",
        "video autoplay "
      );
    }
  } else {
    articleContent.value = null;
  }
};

watch(
  () => interactionConfig.value?.article,
  (value) => {
    if (value || interactionConfig.value?.responseType === "popup") {
      getArticleContent();
    }
  },
  { deep: true }
);

watch(
  () => interactionConfig.value?.responseType,
  (value) => {
    if (value !== "popup") {
      dialogTableVisible.value = false;
      articleContent.value = null;
      return;
    }
    getArticleContent();
  },
  { immediate: true }
);

onMounted(() => {
  getArticleContent();
});

const handleClick = (event) => {
  const responseType = interactionConfig.value?.responseType;
  if (!responseType) {
    return;
  }
  if (responseType === "popup") {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    dialogTableVisible.value = true;
    return;
  }
  if (!["link", "jumpLink"].includes(responseType)) {
    return;
  }
  if (!safeLink.value) {
    return;
  }
  event?.preventDefault?.();
  event?.stopPropagation?.();
  window.open(
    safeLink.value,
    responseType === "jumpLink" ? isTarget_Blank() : "_self"
  );
};
</script>

<style scoped>
.click-config-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
}

.click-config-overlay--interactive {
  cursor: pointer;
}
</style>
